/**
 * Destructive confirm. Screen spec 4.5.
 *
 * THE GOVERNING RULE, and it is enforced by the prop rather than by review:
 * type to confirm is reserved for the irreversible. Using it on reversible
 * actions trains people to type through dialogs, which is exactly what makes
 * it stop working on the one that matters.
 *
 * The enumeration mixes what survives with what does not, because a list of
 * only losses reads as a threat and a person cannot tell from it whether their
 * database is about to go with the project.
 *
 * On mismatch nothing turns red while someone is still typing. The action
 * simply stays inert, which says the same thing without scolding a person
 * halfway through a word.
 *
 * After confirming, the dialog is replaced in place by the narration. It does
 * not close and leave the user staring at an unchanged page wondering whether
 * the thing they were afraid of actually happened.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../primitives/Button';
import { Dialog } from '../primitives/Dialog';
import { Field } from '../primitives/Field';
import { captureEvent } from '../../lib/analytics/posthog';
import { t } from '../../lib/i18n/t';
import '../../styles/components/confirm.css';

export interface Consequence {
  /** survives is a check in the live color, loss is a cross in the down color. */
  kind: 'survives' | 'loss';
  text: string;
}

export interface DestructiveConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** A question containing the object's real name: Delete Herb and Ash? */
  title: string;
  /** The exact string a person has to type. Case sensitive. */
  objectName: string;
  consequences: Consequence[];
  /** Repeats the verb and the object. Never Confirm, never OK, never Yes. */
  confirmLabel: string;
  /** True only when the action cannot be undone. */
  typeToConfirm?: boolean;
  typePrompt?: string;
  /** Replaces the dialog body once the action starts. */
  narration?: ReactNode;
  /** Renders the dialog as a still inside the page, for the kit gallery. */
  inline?: boolean;
}

export function DestructiveConfirm({
  open,
  onClose,
  onConfirm,
  title,
  objectName,
  consequences,
  confirmLabel,
  typeToConfirm = false,
  typePrompt,
  narration,
  inline = false,
}: DestructiveConfirmProps) {
  const [typed, setTyped] = useState('');
  const [running, setRunning] = useState(false);
  const openTimeRef = useRef<number>(0);

  useEffect(() => {
    if (open) {
      openTimeRef.current = Date.now();
      captureEvent('destructive_confirm_opened', {
        title,
        object_name: objectName,
        type_to_confirm: typeToConfirm,
        screen_path: window.location.pathname,
      });
      return;
    }
    setTyped('');
    setRunning(false);
  }, [open, title, objectName, typeToConfirm]);

  const handleClose = () => {
    const duration = Date.now() - openTimeRef.current;
    captureEvent('destructive_confirm_cancelled', {
      title,
      object_name: objectName,
      type_to_confirm: typeToConfirm,
      typed_length: typed.length,
      had_started_typing: typed.length > 0,
      duration_ms: duration,
      screen_path: window.location.pathname,
    });
    onClose();
  };

  const handleConfirm = () => {
    const duration = Date.now() - openTimeRef.current;
    captureEvent('destructive_confirm_executed', {
      title,
      object_name: objectName,
      confirm_label: confirmLabel,
      duration_ms: duration,
      screen_path: window.location.pathname,
    });
    setRunning(true);
    onConfirm();
  };

  const armed = !typeToConfirm || typed === objectName;

  if (running && narration) {
    return (
      <Dialog open={open} onClose={handleClose} title={title} inline={inline}>
        {narration}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      inline={inline}
      footer={
        <>
          <Button variant="outline" onClick={handleClose} data-autofocus="">
            {t('Cancel')}
          </Button>
          <Button
            variant="danger-outline"
            disabled={!armed}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <ul className="confirm__list">
        {consequences.map((item) => (
          <li className="confirm__item" data-kind={item.kind} key={item.text}>
            <span className="confirm__glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
                {item.kind === 'survives' ? (
                  <path
                    d="M3 8.5 6.5 12 13 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            </span>
            <span className="confirm__text">{item.text}</span>
          </li>
        ))}
      </ul>

      {typeToConfirm ? (
        <Field
          label={typePrompt ?? t('Type the project name to confirm')}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          mono
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={objectName}
        />
      ) : null}
    </Dialog>
  );
}
